<template>
  <VDialog v-model="show" max-width="600" persistent>
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>
          <VIcon class="mr-2">mdi mdi-network</VIcon>
          Connection Diagnostics
        </span>
        <VBtn icon="mdi mdi-close" size="small" variant="text" @click="close"/>
      </VCardTitle>
      <VDivider/>

      <VCardText class="pa-4">
        <VList density="compact">
          <VListItem>
            <template #prepend>
              <VIcon :color="statusColor">{{ statusIcon }}</VIcon>
            </template>
            <VListItemTitle>Connection Status</VListItemTitle>
            <VListItemSubtitle>{{ connectionStatus }}</VListItemSubtitle>
          </VListItem>

          <VListItem v-if="socketId">
            <template #prepend>
              <VIcon>mdi mdi-identifier</VIcon>
            </template>
            <VListItemTitle>Socket ID</VListItemTitle>
            <VListItemSubtitle>{{ socketId }}</VListItemSubtitle>
          </VListItem>

          <VListItem>
            <template #prepend>
              <VIcon :color="isAuthenticated ? 'success' : 'error'">
                {{ isAuthenticated ? 'mdi mdi-check-circle' : 'mdi mdi-close-circle' }}
              </VIcon>
            </template>
            <VListItemTitle>Authentication</VListItemTitle>
            <VListItemSubtitle>{{ isAuthenticated ? 'Authenticated' : 'Not Authenticated' }}</VListItemSubtitle>
          </VListItem>

          <VListItem>
            <template #prepend>
              <VIcon>mdi mdi-server</VIcon>
            </template>
            <VListItemTitle>Server</VListItemTitle>
            <VListItemSubtitle>{{ serverUrl }}</VListItemSubtitle>
          </VListItem>

          <VListItem v-if="businessId">
            <template #prepend>
              <VIcon>mdi mdi-briefcase</VIcon>
            </template>
            <VListItemTitle>Business ID</VListItemTitle>
            <VListItemSubtitle>{{ businessId }}</VListItemSubtitle>
          </VListItem>

          <VListItem>
            <template #prepend>
              <VIcon :color="autoReconnect ? 'success' : 'warning'">
                {{ autoReconnect ? 'mdi mdi-sync' : 'mdi mdi-sync-off' }}
              </VIcon>
            </template>
            <VListItemTitle>Auto-Reconnect</VListItemTitle>
            <VListItemSubtitle>{{ autoReconnect ? 'Enabled' : 'Disabled' }}</VListItemSubtitle>
          </VListItem>

          <VListItem v-if="lastError">
            <template #prepend>
              <VIcon color="error">mdi mdi-alert-circle</VIcon>
            </template>
            <VListItemTitle>Last Error</VListItemTitle>
            <VListItemSubtitle class="text-wrap">{{ lastError }}</VListItemSubtitle>
          </VListItem>
        </VList>

        <VAlert v-if="!isConnected" class="mt-4" color="warning" icon="mdi mdi-alert" variant="tonal">
          Socket is disconnected. Auto-reconnect is active and will retry automatically.
        </VAlert>

        <VAlert v-if="isConnecting" class="mt-4" color="info" icon="mdi mdi-loading" variant="tonal">
          Connecting to server...
        </VAlert>
      </VCardText>

      <VDivider/>
      <VCardActions class="pa-4">
        <VBtn color="secondary" variant="outlined" @click="close">
          Close
        </VBtn>
        <VSpacer/>
        <VBtn
          :disabled="isConnecting || isConnected"
          :loading="isReconnecting"
          color="primary"
          prepend-icon="mdi mdi-refresh"
          @click="manualReconnect"
        >
          Retry Connection
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const socketStore = useSocketStore()
const userStore = useUserStore()
const {$sc} = useNuxtApp()

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const isReconnecting = ref(false)

const isConnected = computed(() => socketStore.isConnected)
const isConnecting = computed(() => socketStore.isConnecting)
const isAuthenticated = computed(() => socketStore.isAuthenticated)
const socketId = computed(() => socketStore.socketId)
const connectionStatus = computed(() => socketStore.socketConnectionStatus || 'Unknown')
const lastError = computed(() => socketStore.error)
const businessId = computed(() => userStore.activeBusinessId)
const autoReconnect = computed(() => true)

const statusColor = computed(() => {
  if (isConnected.value && isAuthenticated.value) return 'success'
  if (isConnecting.value) return 'info'
  return 'error'
})

const statusIcon = computed(() => {
  if (isConnected.value && isAuthenticated.value) return 'mdi mdi-check-circle'
  if (isConnecting.value) return 'mdi mdi-loading mdi-spin'
  return 'mdi mdi-close-circle'
})

const serverUrl = computed(() => {
  // First check if we have a stored server URL from actual connection
  if (socketStore.serverUrl) {
    return socketStore.serverUrl
  }

  // Fall back to runtime config
  const config = useRuntimeConfig()
  const secure = config.public.scServerSecure
  const host = config.public.scServer
  const port = config.public.scServerPort

  // Build URL from config values
  if (host && port) {
    const protocol = secure ? 'wss' : 'ws'
    return `${protocol}://${host}:${port}`
  }

  // Ultimate fallback
  return `ws://localhost:8081 (not configured)`
})

const manualReconnect = async () => {
  isReconnecting.value = true
  try {
    const config = useRuntimeConfig()
    await $sc.connect({
      hostname: config.public.scServer || 'localhost',
      port: parseInt(config.public.scServerPort) || 8081,
      secure: config.public.scServerSecure || false,
      reconnect: true
    })
  } catch (error) {
    console.error('[ConnectionStatus] Manual reconnect failed:', error)
  } finally {
    setTimeout(() => {
      isReconnecting.value = false
    }, 2000)
  }
}

const close = () => {
  show.value = false
}
</script>
